import { GensetExt } from '@/classes/scenario/genset/GensetExt';
import { ScenarioGenerator } from '@/dto/scenario/generator/ScenarioGenerator';
import { DtoExt } from '@/classes/scenario/DtoExt';

type PreSizingStatus =
  { done: false, blackout: 0 } |
  { done: true, blackout: number };

export class ScenarioGeneratorExt extends DtoExt {

  public static fromDto(dto: ScenarioGenerator): ScenarioGeneratorExt {
    return new ScenarioGeneratorExt({
      isGeneratorBackup: dto.isGeneratorBackup,

      hasDifferentGeneratorDayAndNightBefore: dto.hasDifferentGeneratorDayAndNightBefore,
      generatorBeforeDay: dto.generatorBeforeDay && GensetExt.fromDto(dto.generatorBeforeDay),
      generatorBeforeNight: dto.generatorBeforeNight && GensetExt.fromDto(dto.generatorBeforeNight),

      hasDifferentGeneratorAfter: dto.hasDifferentGeneratorAfter,

      hasDifferentGeneratorDayAndNightAfter: dto.hasDifferentGeneratorDayAndNightAfter,
      generatorAfterDay: dto.generatorAfterDay && GensetExt.fromDto(dto.generatorAfterDay),
      generatorAfterNight: dto.generatorAfterNight && GensetExt.fromDto(dto.generatorAfterNight),
      isMulti: dto.isMulti,
      hasDifferentTypes: dto.hasDifferentTypes,
      generatorAfterType1: dto.generatorAfterType1 && GensetExt.fromDto(dto.generatorAfterType1),
      generatorAfterType2: dto.generatorAfterType2 && GensetExt.fromDto(dto.generatorAfterType2),
      srLoad: dto.srLoad,
      srPV: dto.srPV,
      srPower: dto.srPower
    });
  }

  public isGeneratorBackup: boolean | null = null;

  public isMulti: boolean | null = null;

  // Begin Mono
  public hasDifferentGeneratorDayAndNightBefore: boolean | null = null;
  public generatorBeforeDay: GensetExt | null = null;
  public generatorBeforeNight: GensetExt | null = null;

  public hasDifferentGeneratorAfter: boolean | null = null;

  public hasDifferentGeneratorDayAndNightAfter: boolean | null = null;
  public generatorAfterDay: GensetExt | null = null;
  public generatorAfterNight: GensetExt | null = null;
  // End Mono only
  // Begin Multi
  public hasDifferentTypes: boolean | null = null;
  public generatorAfterType1: GensetExt | null = null;
  public generatorAfterType2: GensetExt | null = null;

  public srLoad: number | null = null;
  public srPV: number | null = null;
  public srPower: number | null = null;
  public preSizing: PreSizingStatus = { done: false, blackout: 0 };
  // End Multi

  constructor(options?: Partial<ScenarioGeneratorExt> | null) {
    super();
    Object.defineProperty(this, 'optimumPercentage', {
      ...Object.getOwnPropertyDescriptor(ScenarioGeneratorExt.prototype, 'optimumPercentage'),
      enumerable: true
    });
    if (options) {
      DtoExt.assignToThis(this, options);
      if (options.optimumPercentage !== undefined) {
        this.optimumPercentage = options.optimumPercentage;
      }
    }
  }

  public hasSpinningReserve(): boolean {
    return (this.isMulti && (
      this.generatorAfterType1?.srPriority ||
      this.generatorAfterType2?.srPriority)
    ) ?? false;
  }

  public get optimumPercentage(): number | null {
    return this.activeGenerator?.optimumPercentage ?? null;
  }

  public set optimumPercentage(optimum: number | null) {
    if (this.isMulti || this.generatorAfterType1?.optimumPercentage === null) {
      this.generatorAfterType1 && (this.generatorAfterType1.optimumPercentage = optimum);
      this.generatorAfterType2 && (this.generatorAfterType2.optimumPercentage = optimum);
    }
    if (!this.isMulti || this.generatorBeforeDay?.optimumPercentage === null) {
      this.generatorBeforeDay && (this.generatorBeforeDay.optimumPercentage = optimum);
      this.generatorBeforeNight && (this.generatorBeforeNight.optimumPercentage = optimum);
      this.generatorAfterDay && (this.generatorAfterDay.optimumPercentage = optimum);
      this.generatorAfterNight && (this.generatorAfterNight.optimumPercentage = optimum);
    }
  }

  private get activeGenerator(): GensetExt | null {
    if (this.isMulti) {
      return this.generatorAfterType1;
    }
    return this.generatorBeforeDay;
  }

  public toDto(): ScenarioGenerator {
    return new ScenarioGenerator({
      isGeneratorBackup: this.isGeneratorBackup,

      hasDifferentGeneratorDayAndNightBefore: this.hasDifferentGeneratorDayAndNightBefore,
      generatorBeforeDay: this.generatorBeforeDay?.toDto(),
      generatorBeforeNight: this.generatorBeforeNight?.toDto(),

      hasDifferentGeneratorAfter: this.hasDifferentGeneratorAfter,

      hasDifferentGeneratorDayAndNightAfter: this.hasDifferentGeneratorDayAndNightAfter,
      generatorAfterDay: this.generatorAfterDay?.toDto(),
      generatorAfterNight: this.generatorAfterNight?.toDto(),
      isMulti: this.isMulti,
      hasDifferentTypes: this.hasDifferentTypes,
      generatorAfterType1: this.generatorAfterType1?.toDto(),
      generatorAfterType2: this.generatorAfterType2?.toDto(),
      srLoad: this.srLoad,
      srPV: this.srPV,
      srPower: this.srPower
    });
  }
}
